import {useEffect, useState} from 'react';
import {
  Alert,
  Button,
  Col,
  Input,
  Row,
  Spin,
  Typography,
  Select,
  message,
} from 'antd';
import {useHistory, useParams} from 'react-router-dom';
import {NewLevel} from '@PikaDoApp/npm-pikado-logic';
import {LevelsService} from './LevelsService';
import {PlacementTestsService} from '../PlacementTest/PlacementTestsService';
import {getCurrentAuthUserId} from '../../utils';

const levelsService = new LevelsService();
const placementTestsService = new PlacementTestsService();

export const LevelCreateEdit = () => {
  const history = useHistory();
  const {Title, Text} = Typography;
  const params: {levelId?: string} = useParams();

  // Determine if we're in edit mode based on presence of levelId
  const isEditMode = !!params.levelId;

  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [level, setLevel] = useState<NewLevel | null>(null);
  const [levelName, setLevelName] = useState<string>('');
  const [levelDegree, setLevelDegree] = useState<number | undefined>(undefined);
  const [placementTests, setPlacementTests] = useState<any[]>([]);
  const [selectedBusinessPlacementTest, setSelectedBusinessPlacementTest] =
    useState<string | undefined>(undefined);
  const [selectedStudentPlacementTest, setSelectedStudentPlacementTest] =
    useState<string | undefined>(undefined);
  const [isLoadingTests, setIsLoadingTests] = useState<boolean>(false);

  // Fetch placement tests on component mount
  useEffect(() => {
    const fetchPlacementTests = async () => {
      setIsLoadingTests(true);
      try {
        const result = await placementTestsService.getPlacementTests(1, 1000);
        const sortedTests = (result.items || [])
          .map((test: any) => ({
            _id: test._id || test.id,
            name: test.name,
          }))
          .sort((a: any, b: any) => a.name.localeCompare(b.name));
        setPlacementTests(sortedTests);
      } catch (error) {
        console.error('Error fetching placement tests:', error);
        message.error('Failed to load placement tests');
      } finally {
        setIsLoadingTests(false);
      }
    };

    fetchPlacementTests();
  }, []);

  // Fetch level data in edit mode only
  useEffect(() => {
    if (!isEditMode) return;

    const fetchLevel = async () => {
      setIsLoadingData(true);
      setIsError(false);
      try {
        console.log(
          '🔍 [LEVEL-NEW-PACKAGE] Fetching level with ID:',
          params.levelId,
        );
        const fetchedLevel = await levelsService.getNewLevelById(
          params.levelId!,
        );

        if (fetchedLevel) {
          setLevel(fetchedLevel);
          setLevelName(fetchedLevel.name || '');
          setLevelDegree((fetchedLevel as any).degree);
          setSelectedBusinessPlacementTest(
            (fetchedLevel as any).placementTest?.bussinessPlacementTest ||
              (fetchedLevel as any).placementTest?.businessPlacementTest ||
              (fetchedLevel as any).businessPlacementTest ||
              (fetchedLevel as any).bussinessPlacementTest,
          );
          setSelectedStudentPlacementTest(
            (fetchedLevel as any).placementTest?.studentPlacementTest ||
              (fetchedLevel as any).studentPlacementTest,
          );
        } else {
          setIsError(true);
          message.error('Level not found');
        }
      } catch (error) {
        console.error(
          '🔍 [LEVEL-NEW-PACKAGE] Edit page - error loading level:',
          error,
        );
        setIsError(true);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchLevel();
  }, [isEditMode, params.levelId]);

  const handleSubmit = async () => {
    // Validate required fields
    if (!levelName || levelName.trim() === '') {
      message.error('Please enter a level name');
      return;
    }

    if (levelDegree === undefined || levelDegree === null) {
      message.error('Please enter a level degree');
      return;
    }

    setIsSaving(true);

    try {
      if (isEditMode) {
        // Update existing level
        const updateInput: any = {
          id: params.levelId!,
          name: levelName.trim(),
          degree: levelDegree,
        };
        if (selectedBusinessPlacementTest) {
          updateInput.businessPlacementTest = selectedBusinessPlacementTest;
        }
        if (selectedStudentPlacementTest) {
          updateInput.studentPlacementTest = selectedStudentPlacementTest;
        }

        console.log(
          '🔍 [LEVEL-NEW-PACKAGE] Updating level with input:',
          updateInput,
        );
        await levelsService.updateNewLevel(updateInput);
        message.success('Level updated successfully!');
      } else {
        // Create new level
        const levelInput: any = {
          name: levelName.trim(),
          createdBy: getCurrentAuthUserId(),
          degree: levelDegree,
        };
        if (selectedBusinessPlacementTest) {
          levelInput.businessPlacementTest = selectedBusinessPlacementTest;
        }
        if (selectedStudentPlacementTest) {
          levelInput.studentPlacementTest = selectedStudentPlacementTest;
        }

        console.log(
          '🔍 [LEVEL-NEW-PACKAGE] Creating level with input:',
          levelInput,
        );
        await levelsService.createNewLevel(levelInput);
        message.success('Level created successfully!');
      }

      history.push('/levels');
    } catch (error) {
      console.error(
        `🔍 [LEVEL-NEW-PACKAGE] ${isEditMode ? 'Update' : 'Create'} error:`,
        error,
      );
      message.error(
        `Failed to ${isEditMode ? 'update' : 'create'} level. Please try again.`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{padding: 10}}>
      <Title style={{textAlign: 'center'}}>
        {isEditMode ? 'Edit Level' : 'Create Level'}
      </Title>

      {isLoadingData && (
        <div style={{textAlign: 'center', padding: '50px 0'}}>
          <Spin size="large" tip="Loading level..." />
        </div>
      )}

      {isError && (
        <Alert
          message="Error"
          description="Failed to load level. Please try again."
          type="error"
          showIcon
          style={{marginBottom: 20}}
        />
      )}

      {(!isEditMode || (!isLoadingData && !isError && level)) && (
        <Spin
          spinning={isSaving}
          tip={isEditMode ? 'Updating level...' : 'Creating level...'}>
          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Level Name *
                </Text>
                <Input
                  placeholder="Enter Level Name"
                  onChange={e => setLevelName(e.target.value)}
                  value={levelName}
                  onPressEnter={handleSubmit}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Degree *
                </Text>
                <Input
                  type="number"
                  placeholder="Enter Level Degree"
                  onChange={e =>
                    setLevelDegree(
                      e.target.value ? parseInt(e.target.value) : undefined,
                    )
                  }
                  value={levelDegree !== undefined ? levelDegree : ''}
                  onPressEnter={handleSubmit}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Business Placement Test
                </Text>
                <Select
                  placeholder="Select a placement test"
                  allowClear
                  onChange={setSelectedBusinessPlacementTest}
                  value={selectedBusinessPlacementTest}
                  loading={isLoadingTests}
                  disabled={isLoadingTests}
                  optionLabelProp="label">
                  {placementTests.map(test => (
                    <Select.Option
                      key={test._id}
                      value={test._id}
                      label={test.name}>
                      {test.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Student Placement Test
                </Text>
                <Select
                  placeholder="Select a placement test"
                  allowClear
                  onChange={setSelectedStudentPlacementTest}
                  value={selectedStudentPlacementTest}
                  loading={isLoadingTests}
                  disabled={isLoadingTests}
                  optionLabelProp="label">
                  {placementTests.map(test => (
                    <Select.Option
                      key={test._id}
                      value={test._id}
                      label={test.name}>
                      {test.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </Col>
          </Row>

          <div style={{margin: 20}}>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={isSaving}
              disabled={isSaving}>
              {isEditMode ? 'Update Level' : 'Create Level'}
            </Button>
            <Button
              style={{marginLeft: 10}}
              onClick={() => history.push('/levels')}
              disabled={isSaving}>
              Cancel
            </Button>
          </div>
        </Spin>
      )}
    </div>
  );
};
